import { Download, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { usePhotosActions } from './usePhotosActions';

type PhotosLeadingActionsProps = {
  setShowDownloadModal: (show: boolean) => void;
  /** Withheld by the page when the user may not upload here (DV-2820). */
  onOpenUpload?: () => void;
  /**
   * Force-disables the select-all checkbox and the Download/Upload icon
   * buttons regardless of selection state. Used for Zillow 3D, where the
   * whole control row is disabled rather than removed to avoid a layout
   * shift (QA-round fix).
   */
  disabled?: boolean;
};

// AssetsControlBar's `leadingActions` slot for the Photos tab: the select-all
// checkbox + live count, and the Download/Upload icon buttons. Below lg these
// two buttons relocate into the kebab (PhotosMenuItems) — max-lg:hidden here
// keeps them from doubling up (spec §1.7).
const PhotosLeadingActions = ({
  setShowDownloadModal,
  onOpenUpload,
  disabled = false
}: PhotosLeadingActionsProps) => {
  const {
    editEnabled,
    imageCount,
    hasSelection,
    selectedCount,
    hasPhotos,
    isGlobalChecked,
    isIndeterminate,
    downloadLabel,
    toggleSelectAll
  } = usePhotosActions();

  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2">
        <Tooltip delayDuration={250}>
          <TooltipTrigger asChild>
            {/* div wrapper: Radix Tooltip and Radix Checkbox both set their
                own `data-state` attr. With asChild on the Checkbox directly,
                Tooltip's data-state overwrites Checkbox's, killing the
                indeterminate styling. */}
            <div className="inline-flex">
              <Checkbox
                checked={isIndeterminate ? 'indeterminate' : isGlobalChecked}
                onCheckedChange={toggleSelectAll}
                disabled={disabled}
                aria-label="Select All Photos"
              />
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom">Select All</TooltipContent>
        </Tooltip>
        <div className="flex flex-col leading-5 gap-0.5">
          <Typography variant="body-semibold" className="text-foreground">
            {imageCount} {imageCount === 1 ? 'Photo' : 'Photos'}
          </Typography>
          {hasSelection && (
            <Typography variant="body" className="text-muted-foreground">
              {selectedCount} selected
            </Typography>
          )}
        </div>
      </div>
      <Tooltip delayDuration={250}>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setShowDownloadModal(true)}
            // An empty selection downloads "all"; any selection downloads
            // exactly what's picked. Only disable when there are no photos
            // at all -- unless force-disabled (Zillow).
            disabled={disabled || !hasPhotos}
            aria-label={downloadLabel}
            className="border-input max-lg:hidden"
          >
            <Download className="w-4 h-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">{downloadLabel}</TooltipContent>
      </Tooltip>
      {editEnabled && onOpenUpload && (
        <Tooltip delayDuration={250}>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              onClick={onOpenUpload}
              disabled={disabled}
              aria-label="Upload"
              className="border-input max-lg:hidden"
            >
              <Upload className="w-4 h-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Upload Photos</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};

export default PhotosLeadingActions;
